import { useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "./App";
import { Globe, ChevronLeft, Smartphone } from "lucide-react";

// App Store and Play Store URLs
const APP_STORE_URL = "https://apps.apple.com/ec/app/ecuahome/id6766123392";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.origennet.ecuahome.app";

// Helper function to detect user's operating system
const getUserOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  return 'desktop';
};

// App Store Icons
const AppleIcon = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const PlayStoreIcon = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
  </svg>
);

// Translations for the download page
const downloadTranslations = {
  en: {
    title: "Download Ecuahome",
    subtitle: "Your gateway to Latin American real estate",
    description: "Discover, promote, and connect with property opportunities in Latin America. Available on iOS and Android.",
    downloadFor: "Download for",
    iOS: "iOS",
    android: "Android",
    scanToDownload: "Scan to download",
    orVisitStore: "Or visit the store directly",
    backToHome: "Back to Home",
    features: [
      "Browse properties across Latin America",
      "Connect with local real estate professionals",
      "Save and share your favorite listings",
      "Available in English and Spanish"
    ],
    featuresTitle: "Why Ecuahome?"
  },
  es: {
    title: "Descarga Ecuahome",
    subtitle: "Tu puerta de entrada al mercado inmobiliario latinoamericano",
    description: "Descubre, promueve y conecta con oportunidades inmobiliarias en América Latina. Disponible en iOS y Android.",
    downloadFor: "Descargar para",
    iOS: "iOS",
    android: "Android",
    scanToDownload: "Escanea para descargar",
    orVisitStore: "O visita la tienda directamente",
    backToHome: "Volver al Inicio",
    features: [
      "Explora propiedades en toda América Latina",
      "Conecta con profesionales inmobiliarios locales",
      "Guarda y comparte tus listados favoritos",
      "Disponible en inglés y español"
    ],
    featuresTitle: "¿Por qué Ecuahome?"
  }
};

// Logo component
const OrigenLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_web-creator-1089/artifacts/s4fucz7t_ChatGPT%20Image%20Mar%2011%2C%202026%2C%2007_26_54%20PM.png" 
    alt="Origen Tech" 
    className="h-10 w-auto"
  />
);

// Ecuahome Logo component
const EcuahomeLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_web-creator-1089/artifacts/pzfgt8x9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2007_14_26%20PM.png" 
    alt="Ecuahome" 
    className="w-32 h-32 rounded-3xl shadow-2xl"
    style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
  />
);

// Language Toggle for Download Page
const LanguageToggleDownload = () => {
  const { language, toggleLanguage } = useLanguage();
  
  return (
    <button
      onClick={toggleLanguage}
      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all hover:scale-110"
      style={{ 
        background: 'linear-gradient(135deg, #f97316, #ea580c)',
        color: '#ffffff',
        boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
      }}
      data-testid="language-toggle-download"
    >
      <Globe className="w-4 h-4" />
      <span className="uppercase tracking-wide">{language === 'en' ? 'ES' : 'EN'}</span>
    </button>
  );
};

const DownloadPage = () => {
  const { language } = useLanguage();
  const t = downloadTranslations[language];
  const userOS = getUserOS();

  // Auto-redirect mobile users to their respective app store
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const autoRedirect = params.get('auto');
    
    if (autoRedirect === 'true') {
      if (userOS === 'ios') {
        window.location.href = APP_STORE_URL;
      } else if (userOS === 'android') {
        window.location.href = PLAY_STORE_URL;
      }
    }
  }, [userOS]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a1628' }}>
      {/* Header */}
      <header className="py-6 px-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="logo-link">
            <OrigenLogo />
          </a>
          <div className="flex items-center gap-4">
            <LanguageToggleDownload />
            <a 
              href="/"
              className="flex items-center gap-2 text-sm transition-colors"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              <ChevronLeft className="w-4 h-4" />
              {t.backToHome}
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-16">
            {/* Ecuahome App Logo */}
            <div className="flex justify-center mb-8">
              <EcuahomeLogo />
            </div>
            
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6" style={{ backgroundColor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}>
              <Smartphone className="w-5 h-5" style={{ color: '#22c55e' }} />
              <span className="text-sm font-medium" style={{ color: '#22c55e' }}>Mobile App</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#ffffff' }}>
              {t.title}
            </h1>
            <p className="text-xl mb-2" style={{ color: '#22c55e' }}>
              {t.subtitle}
            </p>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {t.description}
            </p>
          </div>

          {/* Download Buttons - Large and Prominent */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* iOS Download */}
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center p-8 rounded-2xl transition-all hover:scale-105"
              style={{ 
                backgroundColor: userOS === 'ios' ? '#ffffff' : 'rgba(255,255,255,0.05)',
                border: userOS === 'ios' ? '2px solid #22c55e' : '1px solid rgba(255,255,255,0.1)',
                boxShadow: userOS === 'ios' ? '0 0 30px rgba(34,197,94,0.3)' : 'none'
              }}
              data-testid="download-ios-button"
            >
              <AppleIcon className={`w-16 h-16 mb-4 ${userOS === 'ios' ? 'text-black' : 'text-white'}`} />
              <span className="text-sm mb-1" style={{ color: userOS === 'ios' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)' }}>
                {t.downloadFor}
              </span>
              <span className="text-2xl font-bold" style={{ color: userOS === 'ios' ? '#000000' : '#ffffff' }}>
                {t.iOS}
              </span>
              <span className="text-sm mt-2" style={{ color: userOS === 'ios' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)' }}>
                App Store
              </span>
            </a>

            {/* Android Download */}
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center p-8 rounded-2xl transition-all hover:scale-105"
              style={{ 
                backgroundColor: userOS === 'android' ? '#ffffff' : 'rgba(255,255,255,0.05)',
                border: userOS === 'android' ? '2px solid #22c55e' : '1px solid rgba(255,255,255,0.1)',
                boxShadow: userOS === 'android' ? '0 0 30px rgba(34,197,94,0.3)' : 'none'
              }}
              data-testid="download-android-button"
            >
              <PlayStoreIcon className={`w-16 h-16 mb-4 ${userOS === 'android' ? 'text-black' : 'text-white'}`} />
              <span className="text-sm mb-1" style={{ color: userOS === 'android' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)' }}>
                {t.downloadFor}
              </span>
              <span className="text-2xl font-bold" style={{ color: userOS === 'android' ? '#000000' : '#ffffff' }}>
                {t.android}
              </span>
              <span className="text-sm mt-2" style={{ color: userOS === 'android' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)' }}>
                Google Play
              </span>
            </a>
          </div>

          {/* QR Codes for Desktop Users */}
          {userOS === 'desktop' && (
            <div className="rounded-2xl p-8 mb-16" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <h2 className="text-xl font-semibold text-center mb-6" style={{ color: '#ffffff' }}>
                {t.scanToDownload}
              </h2>
              <div className="flex flex-wrap justify-center gap-12">
                {/* iOS QR */}
                <div className="flex flex-col items-center gap-3">
                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#ffffff' }}>
                    <QRCodeSVG value={APP_STORE_URL} size={150} level="M" />
                  </div>
                  <div className="flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    <AppleIcon className="w-5 h-5" />
                    <span className="font-medium">{t.iOS}</span>
                  </div>
                </div>
                
                {/* Android QR */}
                <div className="flex flex-col items-center gap-3">
                  <div className="p-4 rounded-xl" style={{ backgroundColor: '#ffffff' }}>
                    <QRCodeSVG value={PLAY_STORE_URL} size={150} level="M" />
                  </div>
                  <div className="flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    <PlayStoreIcon className="w-5 h-5" />
                    <span className="font-medium">{t.android}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Features */}
          <div className="rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h2 className="text-xl font-semibold text-center mb-6" style={{ color: '#ffffff' }}>
              {t.featuresTitle}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {t.features.map((feature, index) => (
                <div 
                  key={`feature-${index}`}
                  className="flex items-center gap-3 p-4 rounded-xl"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                >
                  <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(34,197,94,0.2)' }}>
                    <ChevronLeft className="w-4 h-4 rotate-180" style={{ color: '#22c55e' }} />
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.8)' }}>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
            © {new Date().getFullYear()} Origen Tech. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DownloadPage;
